import React, { useEffect } from "react";

import { observer } from "mobx-react";
import RootStore from "SandboxViewerComponent/src/stores/RootStore";
import { action, computed, makeObservable, observable } from "mobx";

import {
  buildBaseChangeMaps,
  findFirstUsableOriginalIndex,
  findNextUsableOriginalIndex,
  findLastUsableObservationOriginalIndex,
  mapIndexForChangeMode,
  isChangeModeForIndex,
} from "../../utils/sliderUtils";

import DesktopView from "./DesktopView";
import MobileView from "./MobileView";

class PlayerStore {
  get playing() {
    return !!this.playInterval;
  }

  constructor() {
    this.playInterval = null;
    makeObservable(this, {
      playInterval: observable.ref,
      playing: computed,
    });
  }
}

const store = new PlayerStore();
RootStore.playerStore = store;

const setPlayInterval = action((interval) => {
  store.playInterval = interval;
});

/**
 * KlimaSlider container.
 * Owns all behavior: selection mapping, change-mode logic, and play timer.
 * Renders either DesktopView or MobileView.
 *
 * @param {object} props
 * @param {Array<object>} props.items
 * @param {number} props.selected
 * @param {(idx:number)=>void} props.setSelected
 * @param {number} [props.interval]
 * @param {boolean} props.mobile
 * @param {Array<object>} [props.szenarioItems]
 * @param {number} [props.szenarioIndex]
 * @param {(idx:number)=>void} [props.setSzenarioIndex]
 */
function KlimaSlider({
  items = [],
  selected = -1,
  setSelected,
  interval = 5000,
  mobile,
  szenarioItems = [],
  szenarioIndex = -1,
  setSzenarioIndex = () => {},
  // ForecastToggle props
  CommunicationStore,
  token,
  view,
  map,
}) {
  // Snapshot evaluated during render, before any effects run.
  // useEffect([items]) clears store.playInterval before useEffect([selected])
  // gets a chance to run (effects fire in definition order). Reading
  // store.playing live inside the selected effect would therefore always see
  // false and skip scheduling the next timer. The render-time snapshot
  // captures the intended playing state correctly.
  const wasPlaying = store.playing;

  useEffect(() => {
    if (store.playInterval) {
      window.clearTimeout(store.playInterval);
      setPlayInterval(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const originalItems = items;
  const sliderItems = (originalItems || []).filter(
    (item) => item.excludeInSlider !== true
  );

  const { baseToChangeMap, changeToBaseMap } =
    buildBaseChangeMaps(originalItems);

  useEffect(() => {
    if (
      (selected === -1 || selected == null) &&
      sliderItems &&
      sliderItems.length
    ) {
      const lastObs = findLastUsableObservationOriginalIndex(
        originalItems,
        sliderItems
      );
      if (lastObs !== -1) {
        setSelected(lastObs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Cancels any pending timer, schedules the next playback step, and returns the
  // new timer ID so the caller can clear it on cleanup.
  const scheduleNextStep = () => {
    window.clearTimeout(store.playInterval);
    const id = window.setTimeout(() => {
      const nextIndex = findNextUsableOriginalIndex(
        originalItems,
        selected,
        sliderItems
      );
      if (nextIndex !== -1) {
        setSelected(nextIndex);
      }
    }, interval);
    setPlayInterval(id);
    return id;
  };

  useEffect(() => {
    if (!wasPlaying) return;
    const id = scheduleNextStep();
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const isChangeMode = isChangeModeForIndex(originalItems, selected);

  const computeDisplaySelectedOriginalIndex = () => {
    if (isChangeMode && changeToBaseMap.has(selected)) {
      return changeToBaseMap.get(selected);
    }
    return selected;
  };

  const displaySelected = computeDisplaySelectedOriginalIndex();

  const handleChangeMode = (enabled) => {
    const currentItem = originalItems && originalItems[selected];
    if (!currentItem) return;
    const newIndex = mapIndexForChangeMode(originalItems, selected, enabled);
    if (typeof newIndex === "number" && newIndex !== selected)
      setSelected(newIndex);
  };

  const isProjectionSelected = !!originalItems?.[selected]?.projection;
  const compactControlsDisabled = !isProjectionSelected;

  const onPickOriginalIndex = (originalIndex) => {
    if (isChangeMode && baseToChangeMap.has(originalIndex)) {
      setSelected(baseToChangeMap.get(originalIndex));
    } else {
      setSelected(originalIndex);
    }
  };

  const onTogglePlaying = () => {
    if (store.playing) {
      window.clearTimeout(store.playInterval);
      setPlayInterval(null);
    } else if (sliderItems.length) {
      if (selected === -1) {
        const firstIndex = findFirstUsableOriginalIndex(
          originalItems,
          sliderItems
        );
        if (firstIndex !== -1) setSelected(firstIndex);
      }
      scheduleNextStep();
    }
  };

  const playDisabled = sliderItems.length === 0 || sliderItems.every((item) => !item.used);

  if (mobile) {
    return (
      <MobileView
        playing={store.playing}
        onTogglePlaying={onTogglePlaying}
        playDisabled={playDisabled}
        sliderItems={sliderItems}
        originalItems={originalItems}
        displaySelected={displaySelected}
        isChangeMode={isChangeMode}
        onPickOriginalIndex={onPickOriginalIndex}
        projectionControlsDisabled={compactControlsDisabled}
        szenarioItems={szenarioItems}
        szenarioIndex={szenarioIndex}
        onScenarioChange={setSzenarioIndex}
        onChangeMode={handleChangeMode}
        CommunicationStore={CommunicationStore}
        token={token}
        view={view}
        map={map}
      />
    );
  }

  return (
    <DesktopView
      playing={store.playing}
      onTogglePlaying={onTogglePlaying}
      playDisabled={playDisabled}
      sliderItems={sliderItems}
      originalItems={originalItems}
      displaySelected={displaySelected}
      isChangeMode={isChangeMode}
      onPickOriginalIndex={onPickOriginalIndex}
      projectionControlsDisabled={compactControlsDisabled}
      szenarioItems={szenarioItems}
      szenarioIndex={szenarioIndex}
      onScenarioChange={setSzenarioIndex}
      onChangeMode={handleChangeMode}
      CommunicationStore={CommunicationStore}
      token={token}
      view={view}
      map={map}
    />
  );
}

export default observer(KlimaSlider);
